import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Users, AlertCircle, TrendingUp, Clock, ChevronRight, CheckCircle } from 'lucide-react';
import { getCalls, getPipelineSummary, getFollowUps } from '../services/api';
import { timeAgo, formatDuration, callTypeBadge, urgencyBadge } from '../utils';
import ForwardingSetup from '../components/ForwardingSetup';

export default function Dashboard() {
  const [calls, setCalls] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getCalls(),
      getPipelineSummary(),
      getFollowUps(),
    ]).then(([callsRes, pipelineRes, followUpsRes]) => {
      setCalls(callsRes.data.calls || []);
      setPipeline(pipelineRes.data || {});
      setFollowUps(followUpsRes.data.contacts || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalCalls = calls.length;
  const salesLeads = calls.filter(c => c.call_type === 'sales_lead').length;
  const recentCalls = calls.slice(0, 8);

  const urlParams = new URLSearchParams(window.location.search);
  const paymentSuccess = urlParams.get('payment') === 'success';
  const upgradedPlan = urlParams.get('plan');

  if (loading) return (
    <div className="loading">
      <div className="spinner" />
    </div>
  );

  return (
    <div className="page-wrap" style={{ maxWidth: 1100 }}>

      {/* Payment success banner */}
      {paymentSuccess && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 20px', borderRadius: 10, marginBottom: 20,
          background: 'rgba(0,200,150,0.1)',
          border: '1px solid rgba(0,200,150,0.3)',
        }}>
          <CheckCircle size={20} color="var(--green)" />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--green)' }}>
              Welcome to {upgradedPlan ? upgradedPlan.charAt(0).toUpperCase() + upgradedPlan.slice(1) : 'Aizaan'}!
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              Your subscription is active. All features are unlocked.
            </div>
          </div>
        </div>
      )}

      <ForwardingSetup />

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
          Dashboard
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Your call intelligence overview
        </p>
      </div>

      {/* Stats — 2 cols on mobile/tablet, 4 on desktop */}
      <div className="stats-grid">
        <div className="stat-card" style={{ borderLeft: '3px solid var(--blue)' }}>
          <Phone size={16} color="var(--blue)" />
          <div className="stat-value">{totalCalls}</div>
          <div className="stat-label">Total Calls</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--green)' }}>
          <TrendingUp size={16} color="var(--green)" />
          <div className="stat-value">{salesLeads}</div>
          <div className="stat-label">Sales Leads</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--orange)' }}>
          <Users size={16} color="var(--orange)" />
          <div className="stat-value">{pipeline.total || 0}</div>
          <div className="stat-label">Total Contacts</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '3px solid var(--red)' }}>
          <AlertCircle size={16} color="var(--red)" />
          <div className="stat-value">{followUps.length}</div>
          <div className="stat-label">Follow-ups Due</div>
        </div>
      </div>

      {/* Main content — stacked on mobile, side-by-side on desktop */}
      <div className="dash-main-grid">

        {/* Recent Calls */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{ fontSize: 14, fontWeight: 600 }}>Recent Calls</h2>
            <Link to="/calls" style={{
              fontSize: 12,
              color: 'var(--blue-light)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}>
              View all <ChevronRight size={14} />
            </Link>
          </div>

          {recentCalls.length === 0 ? (
            <div style={{ padding: 28 }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Phone size={32} color="var(--text-muted)" style={{ marginBottom: 12 }} />
                <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>No calls captured yet</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Complete these steps to start capturing calls automatically
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  { num: 1, text: 'Check your forwarding code was dialled correctly', link: null },
                  { num: 2, text: 'Set up your agents in Team settings', link: '/team' },
                  { num: 3, text: 'Ask someone to call your Aizaan number', link: null },
                ].map(step => (
                  <div key={step.num} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: 'var(--blue)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                    }}>{step.num}</div>
                    <span style={{ fontSize: 13, flex: 1 }}>{step.text}</span>
                    {step.link && (
                      <Link to={step.link} style={{ fontSize: 12, color: 'var(--blue-light)', whiteSpace: 'nowrap' }}>
                        Go →
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="resp-table">
                <thead>
                  <tr>
                    <th>Caller</th>
                    <th>Type</th>
                    <th className="desktop-only">Duration</th>
                    <th>Urgency</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalls.map(call => {
                    const typeBadge = callTypeBadge(call.call_type);
                    const urgBadge = urgencyBadge(call.urgency);
                    return (
                      <tr key={call.id} onClick={() => window.location.href = `/calls/${call.id}`} style={{ cursor: 'pointer' }}>
                        <td>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>
                            {call.caller_name || call.caller_number || '—'}
                          </div>
                          {call.caller_company && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {call.caller_company}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${typeBadge.class}`}>
                            {typeBadge.label}
                          </span>
                        </td>
                        <td className="desktop-only" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                          {formatDuration(call.duration)}
                        </td>
                        <td>
                          <span className={`badge ${urgBadge.class}`}>
                            {urgBadge.label}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {timeAgo(call.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Pipeline */}
          <div className="card">
            <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Pipeline</h2>
            {[
              { key: 'new_lead', label: 'New Leads', color: 'var(--blue)' },
              { key: 'contacted', label: 'Contacted', color: 'var(--cyan)' },
              { key: 'proposal_sent', label: 'Proposal Sent', color: 'var(--orange)' },
              { key: 'won', label: 'Won', color: 'var(--green)' },
              { key: 'lost', label: 'Lost', color: 'var(--text-muted)' },
            ].map(stage => (
              <div key={stage.key} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '7px 0',
                borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 7, height: 7,
                    borderRadius: '50%',
                    background: stage.color,
                    flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {stage.label}
                  </span>
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {pipeline[stage.key] || 0}
                </span>
              </div>
            ))}
          </div>

          {/* Follow-ups Due */}
          <div className="card">
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <h2 style={{ fontSize: 14, fontWeight: 600 }}>Follow-ups Due</h2>
              <span className="badge badge-red">{followUps.length}</span>
            </div>

            {followUps.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                No overdue follow-ups
              </p>
            ) : (
              followUps.slice(0, 5).map(contact => (
                <Link
                  key={contact.id}
                  to={`/contacts/${contact.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border)',
                    textDecoration: 'none',
                  }}
                >
                  <div style={{
                    width: 30, height: 30,
                    background: 'var(--bg-hover)',
                    borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 600, color: 'var(--blue-light)',
                    flexShrink: 0,
                  }}>
                    {(contact.name || contact.phone || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {contact.name || contact.phone}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--red)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={10} />
                      Due {timeAgo(contact.follow_up_date)}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          <TrialBanner />

        </div>
      </div>
    </div>
  );
}

function TrialBanner() {
  const [trial, setTrial] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('cia_token');
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/calls/trial-status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setTrial(data))
      .catch(() => {});
  }, []);

  if (!trial || trial.reason === 'paid_subscription') return null;

  if (trial.reason === 'trial_expired') return (
    <div style={{
      background: 'rgba(239,68,68,0.1)',
      border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8,
      padding: '12px 16px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444', marginBottom: 6 }}>
        Trial expired
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        Your 14-day trial has ended. Upgrade to keep capturing calls.
      </div>
      <a href="/settings" style={{
        display: 'inline-block',
        background: '#ef4444', color: '#fff',
        padding: '6px 14px', borderRadius: 6,
        fontSize: 12, fontWeight: 600,
      }}>
        Upgrade now
      </a>
    </div>
  );

  const days = trial.days_remaining;
  if (days === undefined || days > 7) return null;

  return (
    <div style={{
      background: 'rgba(245,158,11,0.1)',
      border: '1px solid rgba(245,158,11,0.3)',
      borderRadius: 8,
      padding: '12px 16px',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', marginBottom: 4 }}>
        {days === 0 ? 'Trial expires today' : `${days} day${days === 1 ? '' : 's'} left in trial`}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
        Upgrade to keep your calls, recordings, and contacts.
      </div>
      <a href="/settings" style={{
        display: 'inline-block',
        background: '#f59e0b', color: '#fff',
        padding: '6px 14px', borderRadius: 6,
        fontSize: 12, fontWeight: 600,
      }}>
        Upgrade now
      </a>
    </div>
  );
}
